import { addDays, subDays } from 'date-fns'

const MONDAY = 1
const SUNDAY = 0

// View WCA-6840 for comments explaining logic details (PR changes)

/** Normalizes a date to noon to prevent comparison issues */
const normalize = (d: Date): Date => {
  const n = new Date(d)
  n.setHours(12, 0, 0, 0)
  return n
}

/**
 * Returns true when `date` falls within the confirmed range
 * (both startDate and endDate are set).
 */
export const isInConfirmedRange = (date: Date, startDate?: Date, endDate?: Date): boolean => {
  if (!startDate || !endDate) return false
  const start = new Date(startDate)
  start.setHours(0, 0, 0, 0)
  const end = new Date(endDate)
  end.setHours(23, 59, 59, 999)
  const target = normalize(date)
  return target >= start && target <= end
}

/**
 * Returns true when `date` falls within the in-progress selection range
 * (startDate is set, endDate is still null, user is hovering).
 * Supports hover in both directions from startDate.
 */
export const isInSelectingRange = (
  date: Date,
  startDate?: Date,
  endDate?: Date,
  hoverDate?: Date | null
): boolean => {
  if (!startDate || endDate || !hoverDate) return false
  const s = normalize(startDate)
  const h = normalize(hoverDate)
  const target = normalize(date)
  return h >= s ? target >= s && target <= h : target >= h && target <= s
}

export interface ContainerGridBounds {
  /** First visible day of the grid (may belong to the previous month) */
  firstVisible: Date
  /** Last visible day of the grid (may belong to the next month) */
  lastVisible: Date
  /** Last Sunday of the previous container's grid (used for cross-month row connections) */
  prevLastSunday: Date
}

/**
 * Computes the visible grid boundaries for a month container
 * (calendarStartDay = 1, week starts on Monday).
 */
export const getContainerGridBounds = (
  displayedStartDate: Date,
  containerOffset: number
): ContainerGridBounds => {
  const containerMonth = new Date(
    displayedStartDate.getFullYear(),
    displayedStartDate.getMonth() + containerOffset,
    1
  )

  const firstDow = containerMonth.getDay()
  const daysBack = firstDow === 0 ? 6 : firstDow - 1
  const firstVisible = subDays(containerMonth, daysBack)

  const lastDayOfContainer = new Date(
    containerMonth.getFullYear(),
    containerMonth.getMonth() + 1,
    0
  )
  const lastDow = lastDayOfContainer.getDay()
  const daysForward = lastDow === 0 ? 0 : 7 - lastDow
  const lastVisible = addDays(lastDayOfContainer, daysForward)

  const prevContainerMonth = new Date(
    displayedStartDate.getFullYear(),
    displayedStartDate.getMonth() + containerOffset - 1,
    1
  )
  const prevLastDay = new Date(
    prevContainerMonth.getFullYear(),
    prevContainerMonth.getMonth() + 1,
    0
  )
  const prevLastDow = prevLastDay.getDay()
  const prevLastSunday = prevLastDow === 0 ? prevLastDay : addDays(prevLastDay, 7 - prevLastDow)

  return { firstVisible, lastVisible, prevLastSunday }
}

/** Computes grid bounds for all visible containers (index = containerOffset) */
export const getAllContainerBounds = (
  displayedStartDate: Date,
  monthsShownCount: number
): ContainerGridBounds[] =>
  Array.from({ length: monthsShownCount }, (_, offset) =>
    getContainerGridBounds(displayedStartDate, offset)
  )

interface DayRangeFlagsParams {
  date: Date
  startDate?: Date
  endDate?: Date
  hoverDate?: Date | null
  displayedStartDate: Date
  monthsShownCount: number
  /** Pre-computed bounds (getAllContainerBounds) to avoid recalculating per cell */
  bounds: ContainerGridBounds[]
}

export interface DayRangeFlags {
  hasAbove: boolean
  hasBelow: boolean
}

/**
 * Computes vertical connection flags (dayHasAbove / dayHasBelow)
 * for a day cell in the first or last column of the week.
 */
export const getDayRangeFlags = ({
  date,
  startDate,
  endDate,
  hoverDate,
  displayedStartDate,
  monthsShownCount,
  bounds,
}: DayRangeFlagsParams): DayRangeFlags => {
  const isEffectivelyInRange = (d: Date) =>
    isInConfirmedRange(d, startDate, endDate) ||
    isInSelectingRange(d, startDate, endDate, hoverDate)

  const inRange = isEffectivelyInRange(date)
  const dayOfWeek = date.getDay()
  const isFirstCol = dayOfWeek === MONDAY
  const isLastCol = dayOfWeek === SUNDAY

  // Early exit: cell is out of range or not in an edge column — no connections needed
  if (!inRange || (!isFirstCol && !isLastCol)) {
    return { hasAbove: false, hasBelow: false }
  }

  const dayAbove = subDays(date, 7)
  const dayBelow = addDays(date, 7)

  const displayStartTotal = displayedStartDate.getFullYear() * 12 + displayedStartDate.getMonth()
  const dateTotal = date.getFullYear() * 12 + date.getMonth()
  const containerOffset = Math.max(0, Math.min(monthsShownCount - 1, dateTotal - displayStartTotal))

  const { firstVisible, lastVisible, prevLastSunday } = bounds[containerOffset]

  const isFirstSundayOfGrid =
    isLastCol &&
    monthsShownCount > 1 &&
    containerOffset > 0 &&
    normalize(date) >= normalize(firstVisible) &&
    normalize(subDays(date, 7)) < normalize(firstVisible) &&
    isEffectivelyInRange(dayAbove) &&
    dayAbove.getDay() === 0 &&
    normalize(prevLastSunday).getTime() === normalize(dayAbove).getTime()

  const hasAbove =
    isEffectivelyInRange(dayAbove) &&
    (normalize(dayAbove) >= normalize(firstVisible) ||
      (monthsShownCount > 1 && isFirstSundayOfGrid))

  const hasBelow = isEffectivelyInRange(dayBelow) && normalize(dayBelow) <= normalize(lastVisible)

  return { hasAbove, hasBelow }
}
